import { Injectable } from "@angular/core";
import { AppConstants } from "../constants/constants";
import { Modes } from "../constants/modes";
import { IAppConfig } from "../lib/config/app-config";
import { ScormSession } from "../_models/ScormSession";
import { ScreenerResultDto } from "../_models/ScreeneResultDto";

@Injectable({
  providedIn: "root",
})
export class ScormExitSaveService {
  private static readonly screenerPath: string = "PublicScreenerResult/";
  private static readonly trackedPath: string = "ScormEntry/UpdateBulkData";
  private static readonly lessonPath: string =
    "learningtracking/updatebulkdatalesson";
  private static readonly skillCheckPath: string =
    "learningtracking/updatebulkdataskillcheck/";

  // This runs on page unload (reload / tab-close). Angular HttpClient requests are cancelled
  // when the page tears down, so they cannot be used here. The native beacon/keepalive fetch
  // below is what survives an unload, which is why this service intentionally does NOT use
  // HttpClient/Observables — please do not "correct" it back to one.
  flushOnUnload(
    session: ScormSession,
    isComplete: boolean,
    screenerId: number,
    courseId: number,
    config: IAppConfig,
    accessToken: string
  ): void {
    if (!session || !session.open) {
      return;
    }

    const entries = session.scormEntries ?? [];

    if (session.mode === Modes.Screener) {
      if (!isComplete) {
        return;
      }

      entries.forEach((entry) => {
        entry.scormSessionId = 0;
      });

      const dto = new ScreenerResultDto();
      dto.screenerId = screenerId;
      dto.objectId = session.objectId;
      dto.entries = entries;

      const blob = new Blob([JSON.stringify(dto)], {
        type: AppConstants.APPLICATION_JSON,
      });
      navigator.sendBeacon(config.publicBaseUrl + ScormExitSaveService.screenerPath, blob);
      return;
    }

    entries.forEach((entry) => {
      entry.scormSessionId = session.id;
      entry.learnerCourseLogId = session.learnerCourseLogId;
    });

    if (!accessToken) {
      return;
    }

    const path: string = this.getTrackedPath(session.mode, courseId);

    fetch(config.protectedResourceEndpoint + path, {
      method: AppConstants.PUT,
      keepalive: true,
      credentials: "include",
      headers: {
        [AppConstants.CONTENT_TYPE_HEADER]: AppConstants.APPLICATION_JSON,
        [AppConstants.AUTHORIZATION_HEADER]: AppConstants.BEARER_PREFIX + accessToken,
      },
      body: JSON.stringify(entries),
    }).catch(() => {});
  }

  private getTrackedPath(mode: Modes, courseId: number): string {
    switch (mode) {
      case Modes.LessonTracked:
        return ScormExitSaveService.lessonPath;
      case Modes.SkillCheck:
        return ScormExitSaveService.skillCheckPath + courseId;
      default:
        return ScormExitSaveService.trackedPath;
    }
  }
}
